import { Link } from "react-router-dom";
import { ArrowRight, Compass, GraduationCap, Wrench, ArrowUpRight, Sparkles, LineChart, ShieldCheck, Layers, Users, Building2 } from "lucide-react";
import { Section, Card } from "@/components/Section";
import Testimonials from "@/components/Testimonials";
import EventAnnouncement from "@/components/EventAnnouncement";

const HERO_IMG = "/images/hero-team.jpg";

// Three pillars: the strategic architecture
const PILLARS = [
  {
    key: "advise",
    tag: "01 · Advise",
    title: "Leadership Strategy and Executive Advisory",
    tagline: "Help us clarify priorities and move forward with confidence.",
    body:
      "We help leaders clarify priorities, navigate complexity, strengthen decisions, and prepare their organizations to move forward with confidence. When useful, AI readiness and responsible adoption are integrated to support the leadership strategy.",
    accent: "#C6A85A",
    to: "/services#advise",
    items: [
      "Executive advisory and leadership coaching",
      "Priority clarification and decision support",
      "Organizational strategy and readiness",
      "Responsible AI adoption planning (when useful)",
    ],
  },
  {
    key: "equip",
    tag: "02 · Equip",
    title: "Leadership Training, Speaking, and Facilitation",
    tagline: "Help our leaders and teams apply what matters most.",
    body:
      "We equip leaders and teams through executive learning experiences, strategy labs, institutes, keynotes, retreats, cohorts, and facilitated sessions designed for immediate application.",
    accent: "#E33278",
    to: "/services#equip",
    items: [
      "Executive keynotes and conference sessions",
      "Leadership institutes and multi-session cohorts",
      "Executive-team retreats and facilitated planning",
      "Strategy labs and applied learning experiences",
    ],
  },
  {
    key: "build",
    tag: "03 · Build",
    title: "Leadership Tools, Decision Support, and Digital Solutions",
    tagline: "Help us build practical systems we can actually use.",
    body:
      "We create practical systems, custom assistants, decision-support resources, learning tools, and digital solutions that extend leadership capacity and make complex work easier to manage and execute.",
    accent: "#0F1D3D",
    to: "/services#build",
    items: [
      "Decision-support systems and playbooks",
      "Custom leadership assistants and workflows",
      "Communication, planning, and meeting tools",
      "Learning tools and organizational toolkits",
    ],
  },
];

// What working together looks like
const ENGAGEMENT_STEPS = [
  { n: "01", t: "Understand the work",
    b: "We begin with the responsibilities you already carry, not with technology, tools, or trends." },
  { n: "02", t: "Determine the right path",
    b: "Together we decide whether strategy, training, a built solution, or a mix will make the work easier or more effective." },
  { n: "03", t: "Design & build with you",
    b: "We work alongside your leaders to design, build, and implement. We do not simply present." },
  { n: "04", t: "Leave with practical capacity",
    b: "You walk away with tools, systems, and clarity your team can put to work immediately." },
];

// Sample outputs: proof through methodology, not fabricated case studies
const BUILD_EXAMPLES = [
  "AI-assisted decision-making protocols",
  "Executive communication builders",
  "Leadership thought-partner assistants",
  "Meeting preparation & follow-up systems",
  "Strategic planning tools",
  "Research & executive-brief workflows",
  "Custom AI agents for specific roles",
  "Prompt libraries & leadership toolkits",
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* ─────────────── EVENT ANNOUNCEMENT (top of page) ─────────────── */}
      <EventAnnouncement testid="home-event-announcement" size="hero" />

      {/* ─────────────── HERO ─────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "#0A152C" }}
        data-testid="home-hero"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 82% 12%, rgba(198,168,90,0.14), transparent 55%), radial-gradient(circle at 8% 88%, rgba(227,50,120,0.09), transparent 55%)",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 layr-grid opacity-[0.06]" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32">
          {/* Wordmark */}
          <div className="flex justify-center">
            <img
              src="/brand/layor-lockup-dark.png"
              alt="LAYOR Digital Group"
              className="w-full max-w-[640px] object-contain"
              data-testid="hero-logo"
            />
          </div>

          {/* Category tag */}
          <div className="mt-8 flex justify-center">
            <div
              className="inline-flex items-center gap-3 rounded-full px-5 py-2 text-[10px] uppercase tracking-[0.35em] font-semibold border"
              style={{
                borderColor: "rgba(198,168,90,0.4)",
                color: "#E6C871",
                backgroundColor: "rgba(255,255,255,0.03)",
              }}
            >
              <span>Executive Leadership &amp; Strategic Advisory</span>
            </div>
          </div>

          {/* Headline */}
          <div className="mt-10 md:mt-14 max-w-5xl mx-auto text-center">
            <h1 className="font-display-black text-[2.4rem] sm:text-5xl lg:text-[4.5rem] leading-[1.02] text-white tracking-tight">
              Extend Your Leadership Capacity{" "}
              <span className="block">
                With <span style={{ color: "#C6A85A" }}>Practical AI</span>
                <span style={{ color: "#E33278" }}>.</span>
              </span>
            </h1>
              <p className="mt-8 text-base md:text-xl leading-[1.6] text-white/80 max-w-3xl mx-auto">
              LAYOR Digital Group is a premium leadership-first executive advisory and transformation firm. We help leaders diagnose complex enterprise challenges, design disciplined strategy, and translate practical AI and digital solutions into measurable outcomes.
            </p>

            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link
                to="/contact"
                className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
                data-testid="hero-book-consultation"
              >
                Book a Consultation <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold border border-white/25 text-white hover:bg-white hover:text-[var(--layor-navy)] transition-colors"
                data-testid="hero-explore"
              >
                Explore How We Help
              </Link>
            </div>
          </div>

          {/* Editorial philosophy strip */}
          <div className="relative mx-auto mt-16 md:mt-24 max-w-4xl">
            <div
              className="rounded-2xl border p-8 md:p-10 text-center"
              style={{
                borderColor: "rgba(198,168,90,0.25)",
                backgroundColor: "rgba(255,255,255,0.03)",
                backdropFilter: "blur(6px)",
              }}
            >
              <div
                className="text-[10px] uppercase tracking-[0.35em] font-semibold"
                style={{ color: "#C6A85A" }}
              >
                Core Philosophy
              </div>
              <p className="mt-4 font-display text-xl md:text-2xl leading-[1.35] text-white/95">
                Diagnose before we design.
                <br />
                Strategize before we build.
                <br />
                Execute with{" "}
                <span style={{ color: "#E6C871" }}>absolute discipline</span>.
              </p>
              <p className="mt-5 text-sm md:text-base text-white/70 max-w-2xl mx-auto">
                Success is measured strictly by realization of the intended enterprise outcome.
              </p>
            </div>
          </div>
        </div>

        {/* Gold + pink bottom rule */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
          <div className="flex-[4]" style={{ backgroundColor: "#C6A85A" }} />
          <div className="flex-1" style={{ backgroundColor: "#E33278" }} />
        </div>
      </section>

      {/* ─────────────── THREE PHILOSOPHY PILLARS ─────────────── */}
      <Section
        id="philosophy"
        eyebrow="How LAYOR Thinks"
        title="Three principles that shape every engagement."
        kicker="Before we recommend a tool, a training, or a system, we hold the work to these three standards."
      >
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {[
            {
              n: "01",
              t: "Leadership as the Foundation",
              b: "Every engagement begins by listening: to the responsibilities you carry, the outcomes you're accountable for, and the people you lead. Leadership sets the direction; technology supports it.",
              accent: "#C6A85A",
            },
            {
              n: "02",
              t: "Philosophy of Execution",
              b: "Great strategy earns its value in the doing. LAYOR moves engagements from diagnosis to design to build to measured outcome, with clear checkpoints and disciplined follow-through along the way.",
              accent: "#E33278",
            },
            {
              n: "03",
              t: "Pragmatic Innovation",
              b: "AI and digital tools are integrated when they genuinely expand capacity, sharpen decisions, or ease execution. Innovation always serves the enterprise outcome and the people doing the work.",
              accent: "#0F1D3D",
            },
          ].map(({ n, t, b, accent }) => (
            <div
              key={n}
              className="rounded-2xl border border-[var(--layor-border)] bg-white p-7 md:p-8"
              data-testid={`philosophy-${n}`}
            >
              <div
                className="font-display-black text-4xl leading-none"
                style={{ color: accent }}
              >
                {n}
              </div>
              <h3 className="mt-5 font-display text-xl text-[var(--layor-navy)] leading-snug">
                {t}
              </h3>
              <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ─────────────── AUTHORITY / WHY PATRICIA ─────────────── */}
      <section
        className="relative overflow-hidden border-y border-[var(--layor-border)]"
        style={{ backgroundColor: "#F6F5F0" }}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-5">
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[var(--layor-gray)] border border-[var(--layor-border)] shadow-[0_40px_100px_-40px_rgba(10,21,44,0.4)]">
                  <img
                    src="/brand/patricia-royal.jpg"
                    alt="Patricia Royal, Ed. S., Founder of LAYOR Digital Group"
                    className="w-full h-full object-cover"
                    data-testid="authority-photo"
                  />
                </div>
                <div className="absolute -bottom-5 left-6 right-6 md:left-8 md:right-8 bg-[var(--layor-navy)] text-white rounded-2xl p-5 border border-[var(--layor-gold)]/40 shadow-[0_20px_50px_-20px_rgba(10,21,44,0.5)]">
                  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#E6C871" }}>
                    Founder
                  </div>
                  <div className="mt-1 font-display text-lg leading-snug">
                    Patricia Royal, Ed. S.
                  </div>
                  <div className="text-xs text-white/70 mt-0.5">
                    30-plus years of progressive leadership experience
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.35em] font-semibold text-[var(--layor-pink)]">
                Why Patricia. Why LAYOR.
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.1] text-[var(--layor-navy)]">
                Experience You Can Trust. Innovation That Works for You.
              </h2>
              <p className="mt-6 text-[var(--layor-navy)]/85 leading-relaxed text-[15px] md:text-base">
                Every engagement begins with listening, understanding your priorities, and respecting the work you are responsible for. Patricia brings 30-plus years of progressive leadership experience to help organizations clarify direction, solve complex challenges, and move forward with confidence.
              </p>
              <p className="mt-4 text-[var(--layor-navy)]/85 leading-relaxed text-[15px] md:text-base">
                When AI and advanced digital tools can strengthen the work, LAYOR integrates them thoughtfully to expand leadership capacity, improve decision-making, and support more effective execution. Human judgment, experience, and meaningful connection remain at the center of every solution.
              </p>
              <blockquote className="mt-8 border-l-2 pl-6 italic text-[var(--layor-navy)]/90 leading-relaxed" style={{ borderColor: "#C6A85A" }}>
                "Diagnose before we design. Strategize before we build. Execute with absolute discipline. And measure success strictly by realization of the intended enterprise outcome."
              </blockquote>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                >
                  About Patricia <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact"
                  className="btn-ghost inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                >
                  Book a Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── HOW LAYOR IS DIFFERENT ─────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "#0A152C" }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 20% 30%, rgba(198,168,90,0.14), transparent 55%), radial-gradient(circle at 80% 70%, rgba(227,50,120,0.10), transparent 55%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <div
              className="text-[10px] uppercase tracking-[0.35em] font-semibold"
              style={{ color: "#E6C871" }}
            >
              How LAYOR Is Different
            </div>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.1] text-white">
              A thoughtful, listening-first executive advisory and transformation firm that also equips and builds.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-4 md:gap-6">
            {[
              {
                k: "Grounded in real experience",
                v: "Every engagement is anchored in decades of executive leadership, informed by what actually works in complex organizations.",
              },
              {
                k: "Listening first, always",
                v: "We start by understanding your priorities, your people, and the work you are already responsible for.",
              },
              {
                k: "Human judgment at the center",
                v: "AI extends leadership capacity. Human judgment, experience, and meaningful connection remain at the heart of every solution.",
              },
              {
                k: "Practical, immediate value",
                v: "Every experience produces something a leader or team can put to work right away, not another initiative on the plate.",
              },
              {
                k: "Responsible by design",
                v: "Privacy, verification, confidentiality, bias awareness, and human oversight are built into every recommendation.",
              },
              {
                k: "Executive-team ready",
                v: "Engagements are shaped for boards, cabinets, executive teams, and organizations that need durable capacity, not one-off sessions.",
              },
            ].map((d, i) => (
              <div
                key={i}
                className="rounded-2xl border p-6 md:p-7"
                style={{
                  borderColor: "rgba(198,168,90,0.28)",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  backdropFilter: "blur(6px)",
                }}
                data-testid={`diff-${i}`}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.35em] font-semibold"
                  style={{ color: "#E6C871" }}
                >
                  {d.k}
                </div>
                <p className="mt-3 text-white/90 leading-relaxed text-[15px]">{d.v}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
          <div className="flex-[4]" style={{ backgroundColor: "#C6A85A" }} />
          <div className="flex-1" style={{ backgroundColor: "#E33278" }} />
        </div>
      </section>

      {/* ─────────────── THREE PILLARS ─────────────── */}
      <Section
        id="pillars"
        eyebrow="How We Help"
        title="Three ways LAYOR extends leadership capacity."
        kicker="Advise, Equip, and Build: separately or as an integrated engagement, always designed around the leader's responsibilities."
      >
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {PILLARS.map((p) => (
            <Link
              key={p.key}
              to={p.to}
              className="group relative rounded-3xl border border-[var(--layor-border)] bg-white p-7 md:p-8 flex flex-col hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(10,21,44,0.3)] transition-all"
              data-testid={`pillar-${p.key}`}
            >
              <div
                className="absolute top-0 left-8 right-8 h-[3px] rounded-b-full"
                style={{ backgroundColor: p.accent }}
              />
              <div
                className="text-[10px] uppercase tracking-[0.35em] font-semibold"
                style={{ color: p.accent }}
              >
                {p.tag}
              </div>
              <h3 className="mt-4 font-display text-2xl text-[var(--layor-navy)] leading-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-[13px] italic text-[var(--layor-pink)]">
                "{p.tagline}"
              </p>
              <p className="mt-4 text-[var(--layor-muted)] leading-relaxed text-sm">
                {p.body}
              </p>
              <ul className="mt-5 space-y-2">
                {p.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-[var(--layor-navy)]">
                    <span
                      className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: p.accent }}
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--layor-navy)] group-hover:text-[var(--layor-pink)] transition-colors">
                  Explore {p.tag.split(" · ")[1] || p.title} <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Digital Studio: dedicated design and production division */}
        <div className="mt-8 rounded-3xl border border-[var(--layor-border)] bg-[var(--layor-ivory)] p-7 md:p-9 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.35em] font-semibold text-[var(--layor-muted)]">
              LAYOR Digital Studio
            </div>
            <h3 className="mt-3 font-display text-xl md:text-2xl text-[var(--layor-navy)] leading-snug">
              The dedicated design and production division of LAYOR Digital Group.
            </h3>
            <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed max-w-2xl">
              LAYOR Digital Studio is the dedicated design and production division of LAYOR Digital Group. We design and produce executive websites, board and pitch decks, presentations, and digital resources. We also refine, update, enhance, or redesign existing assets to ensure they are strategically aligned, professionally presented, and ready to support the organization's goals.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/services#studio"
              className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              About the Studio <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </Section>

      {/* ─────────────── WHAT WE BUILD ─────────────── */}
      <section className="bg-white border-y border-[var(--layor-border)]">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.35em] font-semibold text-[var(--layor-pink)]">
                What We Build
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl leading-[1.1] text-[var(--layor-navy)]">
                Strategic Solutions Built for Immediate Application and Measurable Impact.
              </h2>
              <p className="mt-6 text-[var(--layor-muted)] leading-relaxed">
                Every engagement produces something a leadership team can put to work
                immediately. Below are examples of what clients and workshop participants build
                with LAYOR, always customized to real responsibilities.
              </p>
              <div className="mt-8">
                <Link
                  to="/what-you-will-build"
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                >
                  See the full library <ArrowRight size={14} />
                </Link>
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="grid sm:grid-cols-2 gap-3">
                {BUILD_EXAMPLES.map((item, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-[var(--layor-border)] bg-[var(--layor-ivory)] px-4 py-4 flex items-start gap-3"
                    data-testid={`build-item-${i}`}
                  >
                    <span
                      className="mt-1 h-2 w-2 rounded-full shrink-0"
                      style={{ backgroundColor: i % 2 === 0 ? "#C6A85A" : "#E33278" }}
                    />
                    <span className="text-sm text-[var(--layor-navy)] font-medium leading-snug">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── WORKING TOGETHER ─────────────── */}
      <Section
        id="how"
        eyebrow="Working With LAYOR"
        title="A four-step engagement that respects executive time."
        kicker="Whether the engagement is a single advisory session, an institute, or a multi-month build, the sequence is the same."
      >
        <div className="grid md:grid-cols-4 gap-4 md:gap-5">
          {ENGAGEMENT_STEPS.map((step, i) => (
            <div
              key={step.n}
              className="relative rounded-2xl border border-[var(--layor-border)] bg-white p-7"
              data-testid={`engagement-${i}`}
            >
              <div
                className="font-display-black text-4xl leading-none"
                style={{ color: i === 3 ? "#E33278" : "#C6A85A" }}
              >
                {step.n}
              </div>
              <h3 className="mt-4 font-display text-lg text-[var(--layor-navy)] leading-snug">
                {step.t}
              </h3>
              <p className="mt-2 text-sm text-[var(--layor-muted)] leading-relaxed">
                {step.b}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* ─────────────── TESTIMONIALS (hidden until 3+ approved) ─────────────── */}
      <Testimonials />

      {/* ─────────────── FINAL CTA ─────────────── */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0A152C" }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 15% 20%, rgba(198,168,90,0.18), transparent 55%), radial-gradient(circle at 85% 80%, rgba(227,50,120,0.14), transparent 55%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-5xl px-6 md:px-10 py-20 md:py-28 text-center">
          <div
            className="text-[10px] uppercase tracking-[0.35em] font-semibold"
            style={{ color: "#E6C871" }}
          >
            Ready to Begin
          </div>
          <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.1] text-white">
            Bring your leadership responsibilities into the room. Leave with a plan and the beginnings of a system to carry it out.
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed max-w-2xl mx-auto">
            Every LAYOR engagement begins with a consultation. Share the work you're carrying, and
            we'll tell you honestly whether advisory, training, a built solution, or something
            else is the right next move.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link
              to="/contact"
              className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
              data-testid="cta-book"
            >
              Book a Consultation <ArrowRight size={16} />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold border border-white/25 text-white hover:bg-white hover:text-[var(--layor-navy)] transition-colors"
              data-testid="cta-services"
            >
              Explore How We Help
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
          <div className="flex-1" style={{ backgroundColor: "#E33278" }} />
          <div className="flex-[4]" style={{ backgroundColor: "#C6A85A" }} />
        </div>
      </section>
    </div>
  );
}
